import { getApiUrl } from "../api";
import { useEffect, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { getCustomerRequestNumber } from "../utils/requestReferences";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";

const PAGE_SIZE = 12;
const CART_STORAGE_KEY = "tarahumDonationCart";

const getJwtToken = () => {
  const keys = ["token", "accessToken", "jwt", "authToken"];
  for (const key of keys) {
    const token = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (token?.trim()) return token.replace(/^Bearer\s+/i, "").trim();
  }

  return (process.env.REACT_APP_JWT_TOKEN || "").replace(/^Bearer\s+/i, "").trim();
};


const navigateTo = (path) => {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new Event("app:navigate"));
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const parseAmount = (value) => {
  const amount = Number(String(value || "").replace(/,/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const formatAmount = (value) => parseAmount(value).toLocaleString("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const getDonationPercent = (item) => {
  const amount = parseAmount(item.amount);
  const collected = parseAmount(item.collectedAmount);
  if (!amount || amount <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((collected / amount) * 100)));
};

const getStoredCartItems = () => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    const items = stored ? JSON.parse(stored) : [];
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
};

const mapCase = (item, index, language) => {
  const amount = parseAmount(item.amount);
  const collectedAmount = parseAmount(item.collectedamount || item.collectedAmount);
  const remainingAmount = Math.max(0, amount - collectedAmount);

  return {
    id: item.id || `critical-case-${index}`,
    catid: item.catid || "0",
    projectid: item.id || "0",
    title: stripHtml(language === "ar" ? (item.title_ar || item.title_en || item.title) : (item.title_en || item.title)),
    caseNo: getCustomerRequestNumber(item),
    image: getCloudflareImageUrl(item.ImagePath || item.image, "w=800,sharpen=1"),
    amount,
    collectedAmount,
    remainingAmount,
    categoryTitle: language === "ar" ? (item.category_title_ar || item.category_title_en || item.categoryTitle || "") : (item.category_title_en || item.categoryTitle || ""),
  };
};

const mapProject = (item, index, language) => {
  return {
    id: item.id || `project-${index}`,
    catid: item.catid || "0",
    projectid: item.id || "0",
    title: stripHtml(language === "ar" ? (item.title_ar || item.title_en || item.title) : (item.title_en || item.title)),
    description: stripHtml(language === "ar" ? (item.shortdescription_ar || item.description_ar || item.shortdescription_en || item.description_en || item.description) : (item.shortdescription_en || item.description_en || item.description)),
    image: getCloudflareImageUrl(item.ImagePath || item.image, "w=650,sharpen=1"),
    amount: parseAmount(item.amount),
    categoryTitle: language === "ar" ? (item.category_title_ar || item.category_title_en || item.categoryTitle || "") : (item.category_title_en || item.categoryTitle || ""),
  };
};

export default function DonationProjectsList({ type = "Case", language = "en" }) {
  const interactions = useHomeInteractions();
  const isArabic = language === "ar";
  const isCasePage = type === "Case";
  const pageTitle = isCasePage ? (isArabic ? "الحالات الحرجة" : "Critical Cases") : (isArabic ? "مشاريعنا" : "Our Projects");
  const typeLabel = isCasePage ? (isArabic ? "الحالات الحرجة" : "Critical Cases") : (isArabic ? "المشاريع" : "Projects");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [cartValidation, setCartValidation] = useState({});
  const text = isArabic ? {
    amount: "مبلغ التبرع", remaining: "المبلغ المتبقي", collected: "المبلغ المحصل", enterAmount: "أدخل مبلغ التبرع", currency: "درهم", donate: "تبرع", details: "عرض التفاصيل", addToCart: "أضف إلى السلة", home: "الرئيسية", loading: "جارٍ التحميل...", loadMore: "عرض المزيد", caseNo: "الحالة رقم"
  } : {
    amount: "Donation Amount", remaining: "Remaining amount", collected: "Collected amount", enterAmount: "Enter Your Donation Amount", currency: "AED", donate: "Donate", details: "View Details", addToCart: "Add to Cart", home: "home", loading: "Loading...", loadMore: "Load More", caseNo: "Case no"
  };

  const hasMore = totalRecords > items.length;

  useEffect(() => {
    document.title = `${pageTitle} | Tarahum Charity Foundation`;
  }, [pageTitle]);

  useEffect(() => {
    if (!isArabic) return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => {
      document.body.classList.remove("arabic-layout-page");
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
    };
  }, [isArabic]);

  useEffect(() => {
    const controller = new AbortController();

    const loadPage = async () => {
      setIsLoading(true);
      try {
        const headers = { Accept: "application/json" };
        const token = getJwtToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(getApiUrl(`/api/GetDonationProjectList.ashx?typ=${type}&page=${page}&pageSize=${PAGE_SIZE}`), {
          headers,
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`${pageTitle} request failed with ${response.status}`);
        }

        const result = await response.json();
        const rows = Array.isArray(result?.data) ? result.data : [];
        const mapper = isCasePage ? mapCase : mapProject;
        const mappedItems = rows.map((item, index) => mapper(item, index, language)).filter((item) => item.title);

        setTotalRecords(parseAmount(result?.totalRecords) || mappedItems.length);
        setItems((current) => (page === 1 ? mappedItems : [...current, ...mappedItems]));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn(`Unable to load ${pageTitle}`, error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadPage();

    return () => controller.abort();
  }, [isCasePage, language, page, pageTitle, type]);

  const addToCart = (item, amount, itemType) => {
    const cartItem = {
      id: String(item.id || item.projectid || Date.now()),
      catid: String(item.catid || 0),
      subcatid: "0",
      projectid: String(item.projectid || item.id || 0),
      title: item.title || pageTitle,
      type: itemType,
      typeLabel,
      caseNo: item.caseNo || "",
      categoryTitle: item.categoryTitle || typeLabel,
      amount: String(amount),
      quantity: 1,
      image: item.image || "",
    };

    const existingItems = getStoredCartItems();
    const nextItems = [
      cartItem,
      ...existingItems.filter((existingItem) => existingItem.id !== cartItem.id),
    ];

    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(nextItems));
    navigateTo(isArabic ? "/ar/cart" : "/cart");
  };

  const handleAddCaseToCart = (item, event) => {
    event.preventDefault();

    const card = event.currentTarget.closest(".crtic-body");
    const amountInput = card?.querySelector(".crtic-input-wrap input[type='number']");
    const amount = amountInput?.value?.trim() || "";
    const numericAmount = parseAmount(amount);
    const itemId = String(item.id || item.projectid || "");

    if (!amount || numericAmount <= 0) {
      amountInput?.focus();
      amountInput?.classList.add("is-invalid");
      setCartValidation((current) => ({
        ...current,
        [itemId]: isArabic
          ? "يرجى إدخال مبلغ تبرع صحيح قبل إضافة هذه الحالة إلى السلة."
          : "Please enter a valid donation amount before adding this case to cart.",
      }));
      return;
    }

    amountInput?.classList.remove("is-invalid");
    setCartValidation((current) => {
      const next = { ...current };
      delete next[itemId];
      return next;
    });
    addToCart(item, numericAmount, "critical-case");
  };

  const handleAddProjectToCart = (item, event) => {
    event.preventDefault();

    const card = event.currentTarget.closest(".crtic-body");
    const amountInput = card?.querySelector(".crtic-input-wrap input[type='number']");
    const amount = amountInput?.value?.trim() || "";
    const numericAmount = parseAmount(amount);
    const itemId = String(item.id || item.projectid || "");

    if (!amount || numericAmount <= 0) {
      amountInput?.focus();
      amountInput?.classList.add("is-invalid");
      setCartValidation((current) => ({
        ...current,
        [itemId]: isArabic
          ? "يرجى إدخال مبلغ تبرع صحيح قبل إضافة هذا المشروع إلى السلة."
          : "Please enter a valid donation amount before adding this project to cart.",
      }));
      return;
    }

    amountInput?.classList.remove("is-invalid");
    setCartValidation((current) => {
      const next = { ...current };
      delete next[itemId];
      return next;
    });
    addToCart(item, numericAmount, "project");
  };

  const content = isCasePage
    ? items.map((item, index) => {
        const percent = getDonationPercent(item);
        const validationKey = String(item.id || item.projectid || "");

        return (
          <div className="col-md-4" key={item.id || index}>
            <div className="crtic">
              <div className="crtic-card">
                <div className="crtic-img-wrap">
                  <img src={item.image} alt={item.title} />
                  {item.caseNo ? <div className="crtic-case">{text.caseNo} {item.caseNo}</div> : null}
                </div>
                <div className="crtic-body">
                  <h2 className="crtic-title">{item.title}</h2>

                  <div className="crtic-amount-head">
                    <h3>{text.amount}</h3>
                    <span className="crtic-percent">{percent}%</span>
                  </div>
                  <div className="crtic-progress">
                    <span style={{ width: `${percent}%` }}></span>
                  </div>
                  <div className="crtic-amount-grid">
                    <div className="crtic-amount-box">
                      <span>{text.remaining}</span>
                      <strong>{formatAmount(item.remainingAmount)} {text.currency}</strong>
                    </div>
                    <div className="crtic-amount-box">
                      <span>{text.collected}</span>
                      <strong>{formatAmount(item.collectedAmount)} {text.currency}</strong>
                    </div>
                  </div>

                  <div className="crtic-form">
                    <div className="crtic-input-wrap">
                      <input type="number" placeholder={text.enterAmount} />
                      <span>{text.currency}</span>
                    </div>
                    <button
                      className="crtic-btn crtic-btn-primary openDonation"
                      data-donation-id={item.id}
                      data-catid={item.catid || 0}
                      data-subcatid="0"
                      data-projectid={item.projectid || item.id || 0}
                      data-donation-title={item.title}
                      data-donation-type="critical-case"
                      data-donation-type-label="Critical Cases"
                      data-case-no={item.caseNo || ""}
                      data-category-title={item.categoryTitle || ""}
                      data-donation-amount="100"
                    >
                      {text.donate}
                    </button>
                  </div>

                  <div className="crtic-actions">
                    <Link to={`/critical-cases-details?id=${encodeURIComponent(item.id || "")}`} className="crtic-btn crtic-btn-outline">
                      {text.details}
                    </Link>
                    <button type="button" className="crtic-btn crtic-btn-outline" onClick={(event) => handleAddCaseToCart(item, event)}>
                      {text.addToCart} <i className="fa-solid fa-cart-shopping"></i>
                    </button>
                  </div>
                  {cartValidation[validationKey] ? (
                    <div className="crtic-cart-message" role="alert">
                      <i className="fa-solid fa-circle-exclamation"></i>
                      {cartValidation[validationKey]}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        );
      })
    : items.map((item, index) => {
      const validationKey = String(item.id || item.projectid || "");

      return (
      <div className="col-md-4" key={item.id || index}>
        <div className="crtic">
          <div className="crtic-card">
            <div className="crtic-img-wrap">
              <img src={item.image} alt={item.title} />
            </div>
            <div className="crtic-body">
              <h2 className="crtic-title">{item.title}</h2>
              {item.description ? <p className="crtic-desc">{item.description}</p> : null}

              <div className="crtic-form">
                <div className="crtic-input-wrap">
                  <input type="number" placeholder={text.enterAmount} />
                  <span>{text.currency}</span>
                </div>
                    <button
                      className="crtic-btn crtic-btn-primary openDonation"
                      data-donation-id={item.id}
                      data-catid={item.catid || 0}
                      data-subcatid="0"
                      data-projectid={item.projectid || item.id || 0}
                      data-donation-title={item.title}
                      data-donation-type="project"
                      data-donation-type-label="Projects"
                      data-category-title={item.categoryTitle || ""}
                      data-donation-amount={item.amount || ""}
                    >
                      {text.donate}
                    </button>
              </div>

              <div className="crtic-actions">
                <Link to={`/project-details?id=${encodeURIComponent(item.id || "")}`} className="crtic-btn crtic-btn-outline">
                  {text.details}
                </Link>
                <button type="button" className="crtic-btn crtic-btn-outline" onClick={(event) => handleAddProjectToCart(item, event)}>
                  {text.addToCart} <i className="fa-solid fa-cart-shopping"></i>
                </button>
              </div>
              {cartValidation[validationKey] ? (
                <div className="crtic-cart-message" role="alert">
                  <i className="fa-solid fa-circle-exclamation"></i>
                  {cartValidation[validationKey]}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      );
    });

  return (
    <>
      <Preloader />
      {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}

      <section className="kid-d-wrp pb-0">
        <div className="kid-container">
          <div className="kid-hero">
            <div className="kid-badge">
              <i className="fa-solid fa-box-open"></i>
              {pageTitle}
            </div>
            <h1>{pageTitle}</h1>
            <ul className="kid-breadcrumb">
              <li>
                <Link to="/">{text.home}</Link>
              </li>
              <li>/</li>
              <li>{pageTitle}</li>
            </ul>
          </div>
        </div>
      </section>

      <div className="Critical-Cases-w">
        <div className="container">
          <div className="row donation-projects-grid">{content}</div>

          {hasMore ? (
            <div className="text-center mt-4">
              <button className="btn-default" type="button" onClick={() => setPage((current) => current + 1)} disabled={isLoading}>
                {isLoading ? text.loading : text.loadMore}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {isArabic ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
